import { useState, useEffect } from "react";

// Third Party
import { Formik } from "formik";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";

// UI
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";

import { useToast } from "@/components/ui/use-toast";

// Icons
import { ArrowLeft, Upload } from "lucide-react";

// Services
import { createLaw, updateLaw } from "@/services/law";
import { handleUploadFile } from "@/services/presign-url";
import { getLawTypes } from "@/services/master";

// Types
import type { Law, MutateLaw } from "@/types/law";

export const Route = createFileRoute("/_layout-private/law/mutate")({
	component: RouteComponent,
	validateSearch: (search: Record<string, unknown>) => {
		return {
			id: search.id as string | undefined,
			data: search.data as string | undefined,
		};
	},
});

function RouteComponent() {
	const { id, data } = Route.useSearch();

	const navigate = useNavigate();
	const { toast } = useToast();
	const queryClient = useQueryClient();

	const [parseData, setParseData] = useState({} as Law);
	const [selectedFile, setSelectedFile] = useState<File>();
	const [fileName, setFileName] = useState("");

	// Query
	const { data: lawTypesResponse, isLoading: lawTypesLoading } = useQuery({
		queryKey: ["lawTypes"],
		queryFn: () => getLawTypes(),
	});

	useEffect(() => {
		if (data) {
			console.log(JSON.parse(data), "Data");
			const parseData = JSON.parse(data);
			setParseData(parseData);
			// setSelectedFile(parseData.fileUrl);
		}
	}, [data]);

	// Mutations
	const createLawMutation = useMutation<unknown, unknown, MutateLaw>({
		mutationFn: (formData: MutateLaw) => createLaw(formData),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["laws"] });
			toast({
				title: "ສຳເລັດ",
				description: "ເພີ່ມກົດໝາຍ ແລະ ນິຕິກຳສໍາເລັດແລ້ວ",
			});
			navigate({ to: "..", replace: true });
		},
		onError: () => {
			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່",
			});
		},
	});

	const updateLawMutation = useMutation<unknown, unknown, MutateLaw>({
		mutationFn: (formData: MutateLaw) => updateLaw(id, formData),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["laws"] });

			toast({
				title: "ສຳເລັດ",
				description: "ແກ້ໄຂກົດໝາຍ ແລະ ນິຕິກຳສໍາເລັດແລ້ວ",
			});
			navigate({ to: "..", replace: true });
		},
		onError: () => {
			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່",
			});
		},
	});

	const uploadMutation = useMutation({
		mutationFn: handleUploadFile,
		onSuccess: (filename) => {
			console.log("Upload successful, filename:", filename);

			setFileName(filename);

			toast({
				title: "ສຳເລັດ",
				description: "ອັບໂຫຼດຂໍ້ມູນສໍາເລັດແລ້ວ",
			});
		},
		onError: (error) => {
			console.error("Upload failed:", error);

			const fileInput = document.getElementById("fileUrl") as HTMLInputElement;
			if (fileInput) fileInput.value = "";

			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່",
				variant: "destructive",
			});
		},
	});

	const mutateLoading =
		createLawMutation.isPending || updateLawMutation.isPending;

	if (lawTypesLoading) {
		return <div>Loading...</div>;
	}

	const lawTypes = lawTypesResponse?.data;
	console.log(lawTypes, "law Types");

	return (
		<>
			<Breadcrumb>
				<BreadcrumbList>
					<BreadcrumbItem>
						<BreadcrumbLink>
							<Link replace to="..">
								ຈັດການກົດໝາຍ ແລະ ນິຕິກຳ
							</Link>
						</BreadcrumbLink>
					</BreadcrumbItem>
					<BreadcrumbSeparator />

					<BreadcrumbItem>
						<BreadcrumbPage>ເພີ່ມກົດໝາຍ ແລະ ນິຕິກຳ</BreadcrumbPage>
					</BreadcrumbItem>
				</BreadcrumbList>
			</Breadcrumb>
			<section className="mt-[32px]">
				<h2 className="text-2xl font-semibold">ເພີ່ມກົດໝາຍ ແລະ ນິຕິກຳ</h2>
				<p className="text-muted-foreground">ປ້ອນກົດໝາຍ ແລະ ນິຕິກຳ</p>
				<Separator className="my-6" />
				<button
					type="button"
					onClick={() =>
						navigate({
							to: "..",
							replace: true,
						})
					}
					className="flex items-center gap-[4px]"
				>
					<ArrowLeft className="w-5 h-5" />
					ກັບຄືນ
				</button>
			</section>
			<Formik
				enableReinitialize
				initialValues={{
					name: parseData?.name || "",
					lawTypeId: parseData?.lawTypeId || "",
				}}
				onSubmit={(values: MutateLaw) => {
					const data = {
						...values,
						fileUrl: fileName || parseData?.fileUrl || "",
					};

					id ? updateLawMutation.mutate(data) : createLawMutation.mutate(data);
				}}
			>
				{({ values, handleChange, handleSubmit, setFieldValue }) => (
					<form
						onSubmit={handleSubmit}
						className="max-w-[628px] mx-auto mt-6 space-y-6"
					>
						<section className="space-y-6">
							<header>
								<h3 className="text-[18px] font-semibold">ຂໍ້ມູນທົ່ວໄປ</h3>
								<p className="text-muted-foreground text-[14px]">
									ສິ່ງທີ່ທ່ານປ້ອນໄປຈະຖືກນຳໄປບັນທຶກເປັນຂໍ້ມູນກົດໝາຍ ແລະ ນິຕິກຳ
								</p>
								<Separator className="mt-6" />
							</header>
							<div>
								<Label htmlFor="file_url">
									ເອກະສານ <span className="text-red-500">*</span>
								</Label>
								<div className="w-full h-[500px] rounded-lg max-h-[500px] overflow-hidden border border-dotted bg-zinc-50 border-zinc-200   relative flex items-center flex-col justify-center gap-[24px]">
									{selectedFile ? (
										selectedFile.type === "application/pdf" ? (
											<embed
												src={URL.createObjectURL(selectedFile)}
												type="application/pdf"
												className="object-cover w-full h-[500px]"
											/>
										) : uploadMutation.isPending ? (
											<p>ກຳລັງອັບໂຫຼດເອກະສານ...</p>
										) : selectedFile.type.startsWith("image/") ? (
											<img
												alt="Preview"
												src={URL.createObjectURL(selectedFile)}
												className="object-cover w-full h-full"
											/>
										) : (
											<p>ຟາຍບໍ່ຖືກຮອງຮັບ</p>
										)
									) : (
										<>
											<Upload className="w-8 h-8" />
											<div className="text-center text-[14px]">
												<p>ອັບໂຫຼດເອກະສານ ຫຼື ຮູບພາບ</p>
												<p className="w-full text-muted-foreground text-wrap">
													(ຂະໜາດບໍ່ເກີນ 10MB)
												</p>
											</div>
										</>
									)}

									{!selectedFile ? (
										<input
											type="file"
											name="fileUrl"
											id="fileUrl"
											accept="image/png, image/jpg, image/jpeg, application/pdf"
											className="absolute inset-0 opacity-0 cursor-pointer"
											onChange={(e) => {
												if (e.target.files) {
													setSelectedFile(e.target.files[0]);
													uploadMutation.mutate(e);
												}
											}}
										/>
									) : null}
								</div>
							</div>
							{selectedFile ? (
								<div className="relative flex items-center justify-center mx-auto w-fit">
									<Button variant="outline">ເລືອກເອກະສານໃໝ່</Button>
									<input
										type="file"
										name="fileUrl"
										id="fileUrl"
										accept="image/png, image/jpg, image/jpeg, application/pdf"
										className="absolute inset-0 opacity-0 cursor-pointer"
										onChange={(e) => {
											if (e.target.files) {
												setSelectedFile(e.target.files[0]);
												uploadMutation.mutate(e);
											}
										}}
									/>
								</div>
							) : null}
							<div>
								<Label htmlFor="title">
									ຊື່ເອກະສານ <span className="text-red-500">*</span>
								</Label>
								<Input
									type="text"
									placeholder="ປ້ອນຊື່ເອກະສານ..."
									className="w-full"
									required
									name="name"
									value={values.name}
									onChange={handleChange}
								/>
							</div>

							<div>
								<Label htmlFor="lawTypeId">
									ປະເພດແຈ້ງການ <span className="text-red-500">*</span>
								</Label>
								<Select
									name="lawTypeId"
									onValueChange={(value) => setFieldValue("lawTypeId", value)}
									value={values.lawTypeId}
									defaultValue={values.lawTypeId}
									required
								>
									<SelectTrigger className="w-full">
										<SelectValue placeholder="ເລືອກປະເພດແຈ້ງການ" />
									</SelectTrigger>
									<SelectContent>
										{lawTypes.map((lawType: { id: string; name: string }) => (
											<SelectItem key={lawType.id} value={lawType.id}>
												{lawType.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</section>

						<div className="flex justify-end w-full">
							<Button type="submit" disabled={mutateLoading}>
								{mutateLoading ? "ກຳລັງບັນທຶກຂໍ້ມູນ" : "ບັນທຶກ"}
							</Button>
						</div>
					</form>
				)}
			</Formik>
		</>
	);
}
